import { useRef, useEffect, useState, type ReactNode } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Outlet, useLocation } from 'react-router'
import { layoutStore } from 'shared/layout'
import { BreakpointsEnum } from 'shared/constants/breakpoints'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { ContactsDetails } from 'widgets/ContactsDetails'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { ConversationHeaderSearch } from 'pages/conversations/ui/ConversationHeaderSearch'
import { useConversationsPageLayoutContext } from 'pages/conversations/context/conversationsPageLayoutContext'
import { ConversationHeader } from 'pages/conversations/ui/ConversationHeader/ConversationHeader'
import { ConversationTabs } from 'pages/conversations/ui/ConversationTabs/ConversationTabs'
import styles from './styles.module.scss'

export type ConversationsTemplateContextType = {
  setList: (list: ReactNode | null) => void
}

export const ConversationsTemplateLayout = observer(() => {
  const {
    callModalStore,
    contactCreateModalStore,
    conversationSearchStore,
    contactsDetailsStore,
    conversationMenuStore,
    pageLayoutStore,
    entities,
  } = useConversationsPageLayoutContext()

  const [list, setList] = useState<ReactNode | null>(null)
  const { state } = useLocation()
  const refContentContainer = useRef<HTMLDivElement | null>(null)
  const { activeBreakpoint, isLargeView, isMobileViewWithoutMSize, isMobileView } = layoutStore

  const { isShowContactDetailsOverlay, handleSetVariant } = contactsDetailsGlobalStore
  const showConversationsMenuOverlay = isMobileViewWithoutMSize && !pageLayoutStore.isCollapse
  const showOverlay = isShowContactDetailsOverlay || showConversationsMenuOverlay
  const isContactsDetails = contactsDetailsStore && !isMobileView
  const isOpenConversations = Boolean(contactsDetailsStore?.isVariantOpenConversations)
  const isRCS =
    entities.inboxesStore.currentInbox instanceof Inbox && entities.inboxesStore.currentInbox.isRCS
  const hideTabs = !conversationMenuStore.loadingUser && !isRCS

  handleSetVariant('conversation')

  useEffect(() => {
    if (isOpenConversations) return

    if (isLargeView) {
      contactsDetailsGlobalStore.handleChangeCollapse(false)
      pageLayoutStore.handleChangeCollapse(false)

      return
    }

    if (activeBreakpoint === BreakpointsEnum.M) {
      contactsDetailsGlobalStore.handleChangeCollapse(false)
      pageLayoutStore.handleChangeCollapse(true)

      return
    }

    if (isMobileViewWithoutMSize) {
      contactsDetailsGlobalStore.handleChangeCollapse(true)
      pageLayoutStore.handleChangeCollapse(true)

      return
    }
  }, [activeBreakpoint, isLargeView, isMobileViewWithoutMSize, isOpenConversations])

  useEffect(() => {
    if (refContentContainer.current) {
      pageLayoutStore.setContentContainer(refContentContainer.current)
      if (state?.from === 'openExistingConversation' && layoutStore.isMobileView) {
        pageLayoutStore.toEndContainer()
      }
    }

    return () => {
      conversationSearchStore.clear(false)
      conversationSearchStore.onClose()
    }
  }, [])

  return (
    <div className={styles.wrap}>
      <div className={styles.headerSearchWrap}>
        {conversationSearchStore.isOpened && (
          <ConversationHeaderSearch store={conversationSearchStore} />
        )}

        <div className={styles.searchContentWrap}>
          <div
            className={classnames(styles.contentWrapper, {
              [styles.disabledScroll]: showOverlay,
            })}
          >
            <div className={styles.content} ref={refContentContainer}>
              <div className={styles.listWrap}>
                <ConversationHeader
                  pageLayoutStore={pageLayoutStore}
                  conversationSearchStore={conversationSearchStore}
                  callModalStore={callModalStore}
                  contactCreateModalStore={contactCreateModalStore}
                  conversationMenuStore={conversationMenuStore}
                />

                {hideTabs && <ConversationTabs />}

                {list}
              </div>

              <Outlet
                context={{
                  setList: setList,
                }}
              />
            </div>
          </div>

          {isContactsDetails && <ContactsDetails store={contactsDetailsStore} />}
        </div>
      </div>
    </div>
  )
})
